import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { ServiceModal } from '@/components/ServiceModal';
import { openWhatsApp } from '@/lib/whatsapp';
import { useStore } from '@/contexts/StoreContext';
import {
  Phone,
  MessageCircle,
  MapPin,
  Clock,
  Mail,
  Instagram,
  Facebook,
  Youtube
} from 'lucide-react';

import googlePlay from '@/assets/google-play.png';
import appStore from '@/assets/app-store.png';
import mobiautoIcon from '@/assets/mobiauto_icon.png';
import { getStoreBrandAssets } from '@/lib/storeBrand';
import { hasAppStoreLink as hasResolvedAppStoreLink, resolveAppStoreUrl } from '@/lib/appStoreLinks';
import { StoreFooterIcon } from '@/components/StoreFooterIcon';

interface FooterProps {
  config: any;
}

export const Footer = ({ config }: FooterProps) => {
  const { selectedStore } = useStore();
  const store = selectedStore || config.stores[0];
  const { colors } = useTheme();
  const primaryColor = colors.primary;
  const [selectedService, setSelectedService] = useState<any>(null);
  const hasGooglePlayLink = !!config.googlePlay?.url?.trim();
  const appStoreUrl = resolveAppStoreUrl(config.appStore);
  const hasAppStoreLink = hasResolvedAppStoreLink(config.appStore);

  // Format opening hours from config
  const formatOpeningHours = () => {
    const hours = store.openingHours?.default;
    if (!hours) return [];

    const daysMap: { [key: string]: string } = {
      'sunday': 'Domingo',
      'monday': 'Segunda-feira',
      'tuesday': 'Terça-feira',
      'wednesday': 'Quarta-feira',
      'thursday': 'Quinta-feira',
      'friday': 'Sexta-feira',
      'saturday': 'Sábado'
    };

    const weekDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const formattedHours: { day: string; time: string }[] = [];

    // Check if weekdays have the same hours
    const weekdayHours = weekDays.map(day => hours[day]);
    const allWeekdaysSame = weekdayHours.every(
      (h, i, arr) => h?.from === arr[0]?.from && h?.to === arr[0]?.to
    );

    if (allWeekdaysSame && hours.monday?.from && hours.monday?.to) {
      formattedHours.push({
        day: 'Segunda a Sexta',
        time: `${hours.monday.from} - ${hours.monday.to}`
      });
    } else {
      // Add each weekday separately if they differ
      weekDays.forEach(day => {
        if (hours[day]?.from && hours[day]?.to) {
          formattedHours.push({
            day: daysMap[day],
            time: `${hours[day].from} - ${hours[day].to}`
          });
        }
      });
    }

    // Add Saturday
    if (hours.saturday?.from && hours.saturday?.to) {
      formattedHours.push({
        day: 'Sábado',
        time: `${hours.saturday.from} - ${hours.saturday.to}`
      });
    } else if (!hours.saturday?.from) {
      formattedHours.push({
        day: 'Sábado',
        time: 'Fechado'
      });
    }

    // Add Sunday
    if (hours.sunday?.from && hours.sunday?.to) {
      formattedHours.push({
        day: 'Domingo',
        time: `${hours.sunday.from} - ${hours.sunday.to}`
      });
    } else if (!hours.sunday?.from) {
      formattedHours.push({
        day: 'Domingo',
        time: 'Fechado'
      });
    }

    return formattedHours;
  };

  const openingHours = formatOpeningHours();

  const quickLinks = [
    { label: 'Início', href: '/' },
    { label: 'Estoque', href: '/estoque' },
    { label: 'Zero KM', href: '/estoque?type=zerokm' },
    { label: 'Seminovos', href: '/estoque?type=seminovo' },
    { label: 'Favoritos', href: '/favoritos' },
    { label: 'Serviços', href: '/servicos' },
    { label: 'Contato', href: '/contato' }
  ];

  // Get services dynamically from config
  const getServiceName = (serviceId: string) => {
    const serviceNames: Record<string, string> = {
      '65da42048eca108d87c91da6': 'Agendar Videochamada',
      '65dcb7548eca108d87c91dbb': 'Avaliação',
      '65dcb7c58eca108d87c91dbd': 'Consignação',
      '65dcef468eca108d87c91dc8': 'Consórcio',
      '65dcb6528eca108d87c91db9': 'Despachante',
      '65dcb7898eca108d87c91dbc': 'Financiamento',
      '6628fd535bda2ae51f6fc5ff': 'Peças e Acessórios',
      '65dcef098eca108d87c91dc7': 'Revisões',
      '65dcb7238eca108d87c91dba': 'Seguros',
      '65dceeac8eca108d87c91dc6': 'Serviços',
      '65dcb7fe8eca108d87c91dbe': 'Tabela FIPE',
      '65dcb5e78eca108d87c91db8': 'Test-Drive',
      '65dcb8548eca108d87c91dbf': 'Vendas Diretas'
    };

    return serviceNames[serviceId] || 'Serviço';
  };

  const servicesList = store.services || [];

  const { hasLogo } = getStoreBrandAssets(config, store);

  return (
    <footer
      className="text-white"
      style={{ backgroundColor: primaryColor }}
    >
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">

          {/* Company Info */}
          <div>
            <div className="flex items-center mb-6">
              <StoreFooterIcon
                config={config}
                store={store}
                alt={config.name}
                wrapperClassName={`flex items-center justify-center shrink-0 ${!hasLogo ? 'mr-4' : ''}`}
              />
              {!hasLogo && (
                <div>
                  <h3 className="text-xl font-bold">{config.name}</h3>
                  {config.slogan && <p className="text-primary-foreground/80">{config.slogan}</p>}
                </div>
              )}
            </div>

            <p className="text-primary-foreground/80 mb-6 leading-relaxed">
              Explore nossa seleção de veículos seminovos e zero KM com as melhores condições do mercado e atendimento personalizado.
            </p>

            {/* Social Networks */}
            {config.socialNetworks && (
              <div className="flex gap-4">
                {config.socialNetworks.instagram && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/10 hover:text-white"
                    onClick={() => window.open(config.socialNetworks.instagram, '_blank')}
                    aria-label="Abrir Instagram"
                  >
                    <Instagram className="h-5 w-5" />
                  </Button>
                )}
                {config.socialNetworks.facebook && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/10 hover:text-white"
                    onClick={() => window.open(config.socialNetworks.facebook, '_blank')}
                    aria-label="Abrir Facebook"
                  >
                    <Facebook className="h-5 w-5" />
                  </Button>
                )}
                {config.socialNetworks.youtube && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/10 hover:text-white"
                    onClick={() => window.open(config.socialNetworks.youtube, '_blank')}
                    aria-label="Abrir YouTube"
                  >
                    <Youtube className="h-5 w-5" />
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Links Rápidos</h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  {link.href.startsWith('#') ? (
                    <a
                      href={link.href}
                      className="text-primary-foreground/80 hover:text-primary-foreground transition-colors"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      to={link.href}
                      className="text-primary-foreground/80 hover:text-primary-foreground transition-colors"
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Nossos Serviços</h4>
            <ul className="space-y-3">
              {servicesList.map((service: any, index: number) => (
                <li key={index}>
                  <button
                    onClick={() => setSelectedService(service)}
                    className="text-primary-foreground/80 hover:text-primary-foreground transition-colors text-left underline-offset-4 hover:underline cursor-pointer"
                  >
                    {getServiceName(service.service)}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Contato</h4>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-accent mt-1 flex-shrink-0" />
                <div>
                  <p className="text-primary-foreground/80 text-sm leading-relaxed">
                    {store?.place?.address || 'Endereço da empresa'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-accent flex-shrink-0" />
                <a
                  href={store.phone?.ios}
                  className="text-primary-foreground/80 hover:text-primary-foreground transition-colors"
                >
                  {store.phone?.formatted}
                </a>
              </div>

              <div className="flex items-center gap-3">
                <MessageCircle className="h-5 w-5 text-accent flex-shrink-0" />
                <button
                  onClick={() => openWhatsApp(store.whatsapp?.ios)}
                  className="text-primary-foreground/80 hover:text-primary-foreground transition-colors text-left"
                >
                  {store.whatsapp?.formatted}
                </button>
              </div>

              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-accent flex-shrink-0" />
                <a
                  href={`mailto:${config.email}`}
                  className="text-primary-foreground/80 hover:text-primary-foreground transition-colors"
                >
                  {config.email}
                </a>
              </div>
            </div>

            {/* Opening Hours */}
            <div className="mt-6">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="h-5 w-5 text-accent" />
                <span className="font-semibold">Horário de Funcionamento</span>
              </div>
              <div className="space-y-1 text-sm">
                {openingHours.map((hour, index) => (
                  <div key={index} className="flex justify-between text-primary-foreground/80">
                    <span>{hour.day}:</span>
                    <span>{hour.time}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* App Download Buttons */}
            {(hasGooglePlayLink || hasAppStoreLink) && (
              <div className="mt-8">
                <h4 className="text-lg font-semibold mb-4">Baixe nosso App</h4>
                <div className="flex gap-3">
                  {hasGooglePlayLink && (
                    <a
                      href={config.googlePlay.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src={googlePlay}
                        alt="Disponível no Google Play"
                        width={135}
                        height={40}
                      />
                    </a>
                  )}

                  {hasAppStoreLink && (
                    <a
                      href={appStoreUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src={appStore}
                        alt="Baixar na App Store"
                        width={135}
                        height={40}
                      />
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-primary-foreground/20">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-white/80">
            <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4">
              <p>&copy; {new Date().getFullYear()} {config.name}. Todos os direitos reservados.</p>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/politica-privacidade"
                className="text-white/80 hover:text-white transition-colors underline-offset-4 hover:underline"
              >
                Política de Privacidade
              </Link>
              <span className="hidden md:inline">•</span>
              <Link
                to="/termos-uso"
                className="text-white/80 hover:text-white transition-colors underline-offset-4 hover:underline"
              >
                Termos de Uso
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Service Modal */}
      {selectedService && (
        <ServiceModal
          service={selectedService}
          config={config}
          isOpen={!!selectedService}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedService(null);
            }
          }}
        />
      )}
    </footer>
  );
};